<template>
  <iframe
    :src="src"
    :class="iframeClass"
    :style="iframeStyle"
    frameborder="0"
    ref="adaptiveIframe"
    @load="handleIframeLoad"
    scrolling="no"
  ></iframe>
</template>

<script>
export default {
  name: "AdaptiveIframe",
  props: {
    // iframe 的 src 地址
    src: {
      type: String,
      required: true,
    },
    // 自定义 CSS 类名
    customClass: {
      type: String,
      default: "",
    },
    // 最小高度
    minHeight: {
      type: Number,
      default: 400,
    },
    // 最大高度（设为 null 表示不限制）
    maxHeight: {
      type: Number,
      default: null,
    },

    // 高度调整的边距
    heightMargin: {
      type: Number,
      default: -20,
    },
    // 是否启用自动高度调整
    autoAdjust: {
      type: Boolean,
      default: true,
    },
    // 重试次数
    retryCount: {
      type: Number,
      default: 5,
    },
    // 重试间隔（毫秒）
    retryInterval: {
      type: Number,
      default: 1000,
    },
  },
  data() {
    return {
      currentHeight: this.minHeight,
      retryTimer: null,
      resizeObserver: null,
    };
  },
  computed: {
    iframeClass() {
      return `adaptive-iframe ${this.customClass}`.trim();
    },
    iframeStyle() {
      const style = {
        minHeight: `${this.minHeight}px`,
        height: `${this.currentHeight}px`,
      };

      if (this.maxHeight) {
        style.maxHeight = `${this.maxHeight}px`;
      }

      return style;
    },
  },
  mounted() {
    // 监听窗口大小变化
    window.addEventListener("resize", this.handleResize);
    // 创建 ResizeObserver 监听 iframe 内容变化
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.autoAdjust) {
          this.adjustHeight();
        }
      });
    }
  },
  beforeDestroy() {
    // 清理事件监听器
    window.removeEventListener("resize", this.handleResize);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  },
  watch: {
    src: {
      handler(newVal) {
        if (newVal && this.autoAdjust) {
          this.scheduleHeightAdjustment();
        }
      },
      immediate: false,
    },
  },
  methods: {
    // 处理 iframe 加载完成
    handleIframeLoad() {
      console.log("AdaptiveIframe: iframe 加载完成");
      this.scheduleHeightAdjustment();
    },

    // 调度高度调整
    scheduleHeightAdjustment() {
      // 清除之前的定时器
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
      }

      // 立即调整一次
      this.adjustHeight();

      // 设置多次重试
      for (let i = 1; i <= this.retryCount; i++) {
        setTimeout(() => {
          this.adjustHeight();
        }, this.retryInterval * i);
      }
    },

    // 调整 iframe 高度
    adjustHeight() {
      this.$nextTick(() => {
        const iframe = this.$refs.adaptiveIframe;
        if (!iframe) return;

        try {
          const iframeDoc =
            iframe.contentDocument || iframe.contentWindow.document;
          if (iframeDoc && iframeDoc.body) {
            // 等待一下确保内容完全加载
            setTimeout(() => {
              this.calculateAndSetHeight(iframe, iframeDoc);
            }, 100);
          } else {
            // 如果无法获取文档，使用默认高度
            this.setDefaultHeight(iframe);
          }

        } catch (error) {
          console.log(
            "AdaptiveIframe: 无法获取 iframe 内容高度，可能是跨域问题:",
            error
          );
          this.setDefaultHeight(iframe);
        }
      });
    },

    // 计算并设置高度
    calculateAndSetHeight(iframe, iframeDoc) {
      // 使用更简单准确的高度计算方式
      let height = 0;

      // 优先使用 documentElement 的 scrollHeight
      if (iframeDoc.documentElement && iframeDoc.documentElement.scrollHeight) {
        height = iframeDoc.documentElement.scrollHeight;
      }
      // 如果 documentElement 没有高度，使用 body 的 scrollHeight
      else if (iframeDoc.body && iframeDoc.body.scrollHeight) {
        height = iframeDoc.body.scrollHeight;
      }
      // 最后使用 offsetHeight 作为备选
      else if (
        iframeDoc.documentElement &&
        iframeDoc.documentElement.offsetHeight
      ) {
        height = iframeDoc.documentElement.offsetHeight;
      } else if (iframeDoc.body && iframeDoc.body.offsetHeight) {
        height = iframeDoc.body.offsetHeight;
      }

      // 确保高度不小于最小高度
      height = Math.max(height, this.minHeight);

      // 如果设置了最大高度，确保不超过最大高度
      if (this.maxHeight) {
        height = Math.min(height, this.maxHeight);
      }

      // 添加适当的边距，但不要过度
      const finalHeight = height + Math.min(this.heightMargin, 0);
      this.currentHeight = finalHeight;

      // 强制设置 iframe 内容也隐藏滚动条
      this.hideScrollbars(iframeDoc);

      console.log(
        "AdaptiveIframe: 高度已调整为:",
        finalHeight,
        "原始高度:",
        height
      );

      // 触发高度变化事件
      this.$emit("height-changed", finalHeight);
    },

    // 设置默认高度
    setDefaultHeight(iframe) {
      this.currentHeight = this.minHeight;
      this.$emit("height-changed", this.currentHeight);
    },

    // 隐藏滚动条
    hideScrollbars(iframeDoc) {
      try {
        iframeDoc.body.style.overflow = "hidden";
        iframeDoc.documentElement.style.overflow = "hidden";
        // 强制设置 iframe 内容背景为白色
        iframeDoc.body.style.background = "#fff";
        iframeDoc.documentElement.style.background = "#fff";
        // 添加 CSS 样式到 iframe 内容中
        const style = iframeDoc.createElement("style");
        style.textContent = `
          body, html {
            overflow: hidden !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          ::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          ::-webkit-scrollbar-track {
            display: none !important;
          }
          ::-webkit-scrollbar-thumb {
            display: none !important;
          }
          ::-webkit-scrollbar-corner {
            display: none !important;
          }
        `;
        iframeDoc.head.appendChild(style);
      } catch (e) {
        console.log("AdaptiveIframe: 无法设置 iframe 内容样式，可能是跨域问题");
      }
    },

    // 强制隐藏滚动条
    forceHideScrollbars() {
      this.$nextTick(() => {
        const iframe = this.$refs.adaptiveIframe;
        if (iframe) {
          // 强制设置 iframe 样式
          iframe.style.overflow = "hidden";
          iframe.style.scrollbarWidth = "none";
          iframe.style.msOverflowStyle = "none";

          // 尝试设置 iframe 内容样式
          try {
            const iframeDoc =
              iframe.contentDocument || iframe.contentWindow.document;
            if (iframeDoc) {
              iframeDoc.body.style.overflow = "hidden";
              iframeDoc.documentElement.style.overflow = "hidden";
            }
          } catch (e) {
            // 忽略跨域错误
          }
        }
      });
    },

    // 处理窗口大小变化
    handleResize() {
      if (this.autoAdjust) {
        setTimeout(() => {
          this.adjustHeight();
          this.forceHideScrollbars();
        }, 300);
      }
    },

    // 手动调整高度（供外部调用）
    manualAdjust() {
      this.adjustHeight();
    },

    // 获取当前高度
    getCurrentHeight() {
      return this.currentHeight;
    },
  },
};
</script>

<style scoped lang="scss">
.adaptive-iframe {
  width: 100%;
  border: none;
  background: transparent;
  overflow: hidden !important;
  transition: height 0.3s ease;
  box-sizing: border-box;
  /* 完全隐藏滚动条 */
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
  /* 强制隐藏所有滚动条 */
  scrollbar-gutter: stable;
  /* 确保内容完全显示 */
  display: block;
}

/* 隐藏 Webkit 浏览器的滚动条 */
.adaptive-iframe::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

.adaptive-iframe::-webkit-scrollbar-track {
  display: none !important;
}

.adaptive-iframe::-webkit-scrollbar-thumb {
  display: none !important;
}

.adaptive-iframe::-webkit-scrollbar-thumb:hover {
  display: none !important;
}

.adaptive-iframe::-webkit-scrollbar-corner {
  display: none !important;
}

/* 额外的强制隐藏滚动条规则 */
.adaptive-iframe {
  overflow: hidden !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

/* 确保 iframe 内容也不会出现滚动条 */
.adaptive-iframe iframe {
  overflow: hidden !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}
</style>
